import React from 'react';
import {
	Document, Page, Text, View, StyleSheet, Font
} from '@react-pdf/renderer';
import moment from 'moment';

/* ─── helpers ─────────────────────────────────────────────── */
const fmt = (d, f = 'MMM D, YYYY') => d ? moment(d).format(f) : null;
const arr = (a) => Array.isArray(a) && a.length > 0 ? a : [];
const chipLabel = (item) =>
	typeof item === 'string' ? item : (item.label || item.name || '');

const setupPreferenceLabel = (setupPreference, setupPreferenceOther) => {
	const preference = setupPreference?.[0] || null;
	if (!preference) return setupPreferenceOther || null;
	return preference?.toLowerCase?.() === 'other' ? (setupPreferenceOther || preference) : preference;
};

const toMinutes = (time) => {
	const parsed = moment(time, ['h:mm A', 'HH:mm'], true);
	if (!parsed.isValid()) return Number.MAX_SAFE_INTEGER;
	return (parsed.hours() * 60) + parsed.minutes();
};

const getDurationMinutes = (item) => {
	const start = toMinutes(item.startTime);
	const end = toMinutes(item.endTime);
	if (start === Number.MAX_SAFE_INTEGER || end === Number.MAX_SAFE_INTEGER) {
		return Number.MAX_SAFE_INTEGER;
	}
	if (end >= start) return end - start;
	return (24 * 60 - start) + end;
};

const sortAgendaItems = (items = []) => {
	return [...items].sort((a, b) => {
		const startDiff = toMinutes(a.startTime) - toMinutes(b.startTime);
		if (startDiff !== 0) return startDiff;

		const durationDiff = getDurationMinutes(a) - getDurationMinutes(b);
		if (durationDiff !== 0) return durationDiff;

		const nameA = (a.sessionName || a.roomType || '').toLowerCase();
		const nameB = (b.sessionName || b.roomType || '').toLowerCase();
		return nameA.localeCompare(nameB);
	});
};

const sortAgendaEntriesByDate = (groupedAgenda, baseYear) => {
	return Object.entries(groupedAgenda)
		.map(([dateKey, items]) => ([dateKey, sortAgendaItems(items)]))
		.sort(([dateA], [dateB]) => {
			const parsedA = moment(`${dateA} ${baseYear}`, 'MMM D YYYY', true);
			const parsedB = moment(`${dateB} ${baseYear}`, 'MMM D YYYY', true);

			if (!parsedA.isValid() && !parsedB.isValid()) return dateA.localeCompare(dateB);
			if (!parsedA.isValid()) return 1;
			if (!parsedB.isValid()) return -1;
			return parsedA.valueOf() - parsedB.valueOf();
		});
};

const sortByDate = (rows = []) => {
	return [...rows]
		.map((row, index) => ({ row, index }))
		.sort((a, b) => {
			const aHasDate = !!a.row?.date;
			const bHasDate = !!b.row?.date;

			if (!aHasDate && !bHasDate) return a.index - b.index;
			if (!aHasDate) return 1;
			if (!bHasDate) return -1;

			const aTime = moment(a.row.date).valueOf();
			const bTime = moment(b.row.date).valueOf();

			if (aTime === bTime) return a.index - b.index;
			return aTime - bTime;
		})
		.map(({ row }) => row);
};

/* ─── styles ─────────────────────────────────────────────── */
const s = StyleSheet.create({
	page: {
		fontFamily: 'Helvetica',
		fontSize: 9,
		color: '#1a1a1a',
		padding: '40 48',
		lineHeight: 1.4
	},

	/* header */
	pageHeader: {
		marginBottom: 24,
		borderBottom: '1.5 solid #1a1a1a',
		paddingBottom: 12
	},
	pageTitle: { fontSize: 20, fontFamily: 'Helvetica-Bold', marginBottom: 2 },
	pageSubtitle: { fontSize: 10, color: '#555' },

	/* stats row */
	statsRow: {
		flexDirection: 'row',
		flexWrap: 'wrap',
		gap: 8,
		marginBottom: 20
	},
	statBox: {
		border: '1 solid #e5e7eb',
		borderRadius: 6,
		padding: '8 10',
		minWidth: 90,
		flex: 1
	},
	statLabel: { fontSize: 7, color: '#999', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 2 },
	statValue: { fontSize: 11, fontFamily: 'Helvetica-Bold' },
	statHint:  { fontSize: 7, color: '#aaa', marginTop: 1 },

	/* section cards */
	card: {
		marginBottom: 14,
		border: '1 solid #e5e7eb',
		borderRadius: 8,
		overflow: 'hidden'
	},
	cardTitle: {
		backgroundColor: '#f9fafb',
		borderBottom: '1 solid #e5e7eb',
		padding: '7 12',
		fontFamily: 'Helvetica-Bold',
		fontSize: 9,
		letterSpacing: 0.3
	},
	cardBody: { padding: '8 12' },

	/* field rows */
	fieldRow: {
		flexDirection: 'row',
		paddingVertical: 4,
		borderBottom: '1 solid #f5f5f5'
	},
	fieldLabel: { width: 120, color: '#888', fontSize: 8, fontFamily: 'Helvetica-Bold' },
	fieldValue: { flex: 1, fontSize: 9 },

	/* chips */
	chips: { flexDirection: 'row', flexWrap: 'wrap', gap: 4, paddingVertical: 4 },
	chip: {
		backgroundColor: '#f3f4f6',
		borderRadius: 4,
		padding: '2 6',
		fontSize: 8
	},

	/* room-block table */
	rbHead: {
		flexDirection: 'row',
		backgroundColor: '#f9fafb',
		borderBottom: '1 solid #e5e7eb',
		padding: '4 0'
	},
	rbRow:  { flexDirection: 'row', borderBottom: '1 solid #f5f5f5', padding: '4 0' },
	rbCell: { fontSize: 8 },

	/* agenda */
	agendaGroup: { marginBottom: 8 },
	agendaDate:  { fontSize: 8, fontFamily: 'Helvetica-Bold', color: '#555', marginBottom: 3 },
	agendaRow:   { flexDirection: 'row', gap: 8, paddingVertical: 2, borderBottom: '1 solid #f7f7f7' },
	agendaMeta:  { fontSize: 8, color: '#666' },

	emptyText: { color: '#aaa', fontSize: 8, fontStyle: 'italic', paddingVertical: 4 },

	/* footer */
	footer: {
		position: 'absolute',
		bottom: 24,
		left: 48,
		right: 48,
		flexDirection: 'row',
		justifyContent: 'space-between',
		borderTop: '1 solid #e5e7eb',
		paddingTop: 6
	},
	footerText: { fontSize: 7, color: '#bbb' }
});

/* ─── tiny helpers ────────────────────────────────────────── */
const Field = ({ label, value }) => {
	if (!value) return null;
	return (
		<View style={s.fieldRow}>
			<Text style={s.fieldLabel}>{label}</Text>
			<Text style={s.fieldValue}>{value}</Text>
		</View>
	);
};

const Chips = ({ items }) => {
	const list = arr(items).map(chipLabel).filter(Boolean);
	if (!list.length) return null;
	return (
		<View style={s.chips}>
			{list.map((lbl, i) => <Text key={i} style={s.chip}>{lbl}</Text>)}
		</View>
	);
};

const Card = ({ title, children, empty, wrap = false }) => (
	<View style={[s.card, wrap && { overflow: 'visible' }]} wrap={wrap}>
		<Text style={s.cardTitle}>{title}</Text>
		<View style={[s.cardBody, wrap && { overflow: 'visible' }]}>
			{children || <Text style={s.emptyText}>{empty}</Text>}
		</View>
	</View>
);

/* ─── column widths for room-block table ─────────────────── */
const RB_COLS = [
	{ label: 'Date',  w: '30%' },
	{ label: 'King',  w: '14%' },
	{ label: 'Dbl',   w: '14%' },
	{ label: 'Suite', w: '14%' },
	{ label: 'Staff', w: '14%' },
	{ label: 'Total', w: '14%' },
];

/* ─── main document ─────────────────────────────────────── */
const BriefPDF = ({ request = {} }) => {
	const ed = request.eventDetails || {};
	const rb = request.roomBlock || {};
	const kc = request.keyConsiderations || {};
	const pi = request.plannerInfo || {};
	const ag = request.agencyInfo || {};

	const agendaItems        = arr(request.agendaItems);
	const customConcessions  = arr(request.concessions?.custom);
	const additionalServices = arr(request.additionalServices);

	const statesList      = arr(ed.locations?.states).filter(Boolean);
	const regionsList     = arr(ed.locations?.regions).filter(Boolean);
	const citiesList      = arr(ed.locations?.cities).filter(Boolean);
	const countriesList   = arr(ed.locations?.countries).filter(Boolean);
	const preferredHotels = arr(ed.locations?.hotels).filter(Boolean);
	const plannerName     = [pi.firstName, pi.lastName].filter(Boolean).join(' ') || pi.contactName || null;

	const dailyGrid    = sortByDate(arr(rb.dailyGrid));
	const roomNights   = dailyGrid.length;
	const peakRooms    = roomNights
		? Math.max(...dailyGrid.map(d => (parseInt(d.king) || 0) + (parseInt(d.double) || 0) + (parseInt(d.suite) || 0) + (parseInt(d.staff) || 0)))
		: 0;
	const agendaBaseYear = ed.arrivalDate ? moment(ed.arrivalDate).year() : moment().year();

	/* group agenda by date */
	const groupedAgenda = {};
	agendaItems.forEach((item) => {
		const dates = arr(item.selectedDates);
		const keys  = dates.length ? dates : ['Unassigned'];
		keys.forEach(k => {
			if (!groupedAgenda[k]) groupedAgenda[k] = [];
			groupedAgenda[k].push(item);
		});
	});
	const sortedGroupedAgendaEntries = sortAgendaEntriesByDate(groupedAgenda, agendaBaseYear);

	const eventName = ed.eventName || 'Proposal Request Brief';
	const today     = moment().format('MMMM D, YYYY');

	return (
		<Document title={eventName}>
			<Page size="LETTER" style={s.page}>

				{/* ── Header ── */}
				<View style={s.pageHeader} fixed>
					<Text style={s.pageTitle}>{eventName}</Text>
				</View>
				{/* ── Event Details ── */}
				<Card title="Event Details">
					<Field label="Event Name"    value={ed.eventName} />
					<Field label="Event Type"    value={ed.eventType === 'Other' ? ed.eventTypeOther : ed.eventType} />
					<Field label="Arrival"       value={fmt(ed.arrivalDate)} />
					<Field label="Departure"     value={fmt(ed.departureDate)} />
					{arr(ed.alternateDates).map((d, i) => (
						<Field key={i} label={i === 0 ? 'Alt. Dates' : ''} value={`${fmt(d.arrivalDate)} – ${fmt(d.departureDate)}`} />
					))}
					<Field label="Peak Attendees" value={ed.expectedPeakAttendees > 0 ? String(ed.expectedPeakAttendees) : null} />
					<Field label="Max Room Rate"  value={ed.maxRoomRate ? `$${Number(ed.maxRoomRate).toLocaleString()} / night` : null} />
					<Field label="Star Rating"    value={ed.hotelStarRating ? `${ed.hotelStarRating} Star` : null} />
					{statesList.length > 0 && (
						<View style={s.fieldRow}>
							<Text style={s.fieldLabel}>States</Text>
							<View style={{ flex: 1 }}><Chips items={statesList} /></View>
						</View>
					)}
					{regionsList.length > 0 && (
						<View style={s.fieldRow}>
							<Text style={s.fieldLabel}>Regions</Text>
							<View style={{ flex: 1 }}><Chips items={regionsList} /></View>
						</View>
					)}
					{citiesList.length > 0 && (
						<View style={s.fieldRow}>
							<Text style={s.fieldLabel}>Cities</Text>
							<View style={{ flex: 1 }}><Chips items={citiesList} /></View>
						</View>
					)}
					{countriesList.length > 0 && (
						<View style={s.fieldRow}>
							<Text style={s.fieldLabel}>Countries</Text>
							<View style={{ flex: 1 }}><Chips items={countriesList} /></View>
						</View>
					)}
					{preferredHotels.length > 0 && (
						<View style={s.fieldRow}>
							<Text style={s.fieldLabel}>Preferred Hotels</Text>
							<View style={{ flex: 1 }}><Chips items={preferredHotels} /></View>
						</View>
					)}
					{arr(ed.programDays).length > 0 && (
						<View style={s.fieldRow}>
							<Text style={s.fieldLabel}>Program Days</Text>
							<View style={{ flex: 1 }}><Chips items={ed.programDays} /></View>
						</View>
					)}
					{ed.openToAltArrival && arr(ed.altArrivalDays).length > 0 && (
						<View style={s.fieldRow}>
							<Text style={s.fieldLabel}>Alt. Arrival Days</Text>
							<View style={{ flex: 1 }}><Chips items={ed.altArrivalDays} /></View>
						</View>
					)}
					<Field label="Pattern Notes" value={ed.datePatternNotes} />
				</Card>

				{/* ── Room Block ── */}
				<Card
					title={`Room Block`}
					empty="No room block defined."
				>
					{dailyGrid.length > 0 && (
						<View>
							<View style={s.rbHead}>
								{RB_COLS.map(c => (
									<Text key={c.label} style={[s.rbCell, { width: c.w, fontFamily: 'Helvetica-Bold', fontSize: 7, color: '#888', textTransform: 'uppercase' }]}>
										{c.label}
									</Text>
								))}
							</View>
							{dailyGrid.map((row, i) => {
								const total = (parseInt(row.king) || 0) + (parseInt(row.double) || 0)
									+ (parseInt(row.suite) || 0) + (parseInt(row.staff) || 0);
								return (
									<View key={i} style={s.rbRow}>
										<Text style={[s.rbCell, { width: '30%' }]}>{row.date ? moment(row.date).format('ddd, MMM D') : `Night ${i + 1}`}</Text>
										<Text style={[s.rbCell, { width: '14%' }]}>{row.king || '–'}</Text>
										<Text style={[s.rbCell, { width: '14%' }]}>{row.double || '–'}</Text>
										<Text style={[s.rbCell, { width: '14%' }]}>{row.suite || '–'}</Text>
										<Text style={[s.rbCell, { width: '14%' }]}>{row.staff || '–'}</Text>
										<Text style={[s.rbCell, { width: '14%', fontFamily: 'Helvetica-Bold' }]}>{total || '–'}</Text>
									</View>
								);
							})}
						</View>
					)}
				</Card>

				{/* ── Meeting Space / Agenda ── */}
				<View style={[s.card, { overflow: 'visible', marginBottom: 14 }]} wrap={true} break={dailyGrid.length > 6}>
					<Text style={s.cardTitle}>Meeting Space & Agenda</Text>
					{Object.keys(groupedAgenda).length === 0 && (
						<View style={s.cardBody}>
							<Text style={s.emptyText}>No agenda items added.</Text>
						</View>
					)}
					{Object.keys(groupedAgenda).length > 0 && (
						<View style={[s.cardBody, { overflow: 'visible' }]}>
							{sortedGroupedAgendaEntries.map(([date, items]) => (
								<View key={date} style={s.agendaGroup}>
									<Text style={s.agendaDate}>{date}</Text>
									{items.map((item, i) => (
										<View key={i} style={s.agendaRow} wrap={false} minPresenceAhead={0}>
											{item.startTime && item.endTime && (
												<Text style={s.agendaMeta}>{item.startTime}–{item.endTime}</Text>
											)}
											<Text style={{ fontSize: 9, flex: 1 }}>{item.roomType || `Space ${i + 1}`}</Text>
											{item.attendees > 0 && <Text style={s.agendaMeta}>{item.attendees} att.</Text>}
											{arr(item.setupPreference)[0] && <Text style={s.agendaMeta}>{setupPreferenceLabel(item.setupPreference, item.setupPreferenceOther)}</Text>}
											{item.sessionName && <Text style={[s.agendaMeta, { fontStyle: 'italic' }]}>{item.sessionName}</Text>}
										</View>
									))}
								</View>
							))}
						</View>
					)}
				</View>

				{/* ── Concessions ── */}
				<Card title="Concessions" empty="No concessions requested.">
					{customConcessions.length > 0 && <Chips items={customConcessions} />}
				</Card>

				{/* ── Key Considerations ── */}
				<Card title="Key Considerations" empty={!kc.decisionFactors && !kc.eventHistory && !kc.reservationNotes ? 'No considerations noted.' : null}>
					<Field label="Decision Factors"  value={kc.decisionFactors} />
					<Field label="Event History"     value={kc.eventHistory} />
					<Field label="Reservation Notes" value={kc.reservationNotes} />
				</Card>

				{/* ── Additional Services ── */}
				<Card title="Additional Services" empty="No additional services selected.">
					{additionalServices.length > 0 && <Chips items={additionalServices} />}
				</Card>

				{/* ── Contract Signee ── */}
				<Card title="Contract Signee" empty={!plannerName && !pi.email ? 'No signee info provided.' : null}>
					<Field label="Name"            value={plannerName} />
					<Field label="Title"           value={pi.title} />
					<Field label="Company"         value={pi.companyName} />
					<Field label="Email"           value={pi.email} />
					<Field label="Phone"           value={pi.phone} />
					<Field label="Signer Location" value={pi.signerLocation} />
				</Card>

				{/* ── Agency (optional) ── */}
				{(ag.agencyName || ag.iataNumber) && (
					<Card title="Agency">
						<Field label="Agency Name"    value={ag.agencyName} />
						<Field label="IATA Number"    value={ag.iataNumber} />
						<Field label="Commissionable" value={ag.isCommissionable ? 'Yes' : null} />
					</Card>
				)}

				{/* ── Page footer ── */}
				<View style={s.footer} fixed>
					<Text style={s.footerText}>{eventName}</Text>
					<Text style={s.footerText} render={({ pageNumber, totalPages }) => `Page ${pageNumber} of ${totalPages}`} />
					<Text style={s.footerText}>Generated {today}</Text>
				</View>

			</Page>
		</Document>
	);
};

export default BriefPDF;
